import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Col, Spinner } from "reactstrap";
import { useLocation, useNavigate } from "react-router";
import { ALLOCATION_STATUS, ALLOCATION_STATUS_OPTIONS } from "constants/status";
import {
  canEditRoles,
  fundSourceOptions,
  institutionTypes,
  congRoles,
  BEFORE_TWENTIETH,
  SUPER_ADMIN_ACCOUNTS,
  ACCOUNT_AGENCY,
  ACCOUNT_EDITOR,
} from "utility/constants";
import {
  fundSourceDBMOptions,
  withOtherInfo,
} from "constants/budgetAllocation";
import { AGENCY_DPWH_ID, AGENCY_TESDA_ID } from "constants/agency";
import AsyncSelect from "react-select/async";
import _ from "lodash";
import { fetchAllRegions } from "services/services.congressman";
import {
  Toast,
  getFormattedContactNumber,
  getFormattedDateString,
  getFormattedOptions,
} from "utility/helpers";
import FormGenerator from "components/customs/form-generator/input";
import classNames from "classnames";
import CustomSelect from "components/customs/dropdown";
import {
  fetchInstitutions,
  fetchMunicipalities,
  fetchProvinces,
  updateRow,
} from "services/services.projects";
import { fetchAtcOptions } from "services/services.directory";
import CustomCreatableSelect from "components/customs/creatable-select";
import { congressmanSearch } from "services/services.navbar";
import { officialSearch } from "services/services.official";
import CustomDropzone from "views/projects/add/Dropzone";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { genericGet, genericPost } from "services/services.general";
import CoursesTable from "views/projects/add/CoursesTable";
import {
  addProjectDropdownStyles,
  addProjectSearchInputStyle,
} from "constants/style";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import styled from "styled-components";
import {
  AddButton,
  FormLabel,
  FormRow,
  RequiredIndicator,
} from "assets/js/Styles";
import { withAllocationDetails } from "views/projects/add/constants";
import { ADD_ACTION, EDIT_ACTION } from "constants/actions";
import DpwhTable from "components/forms/recipient/DpwhTable";
import { fetchPendingProjects } from "../../../redux/actions/projects";
import { acceptedFileTypes, selectorOptions } from "../constants";
import { validate, getStatusOptions } from "../utility";

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
`;

const StyledCancelButton = styled(AddButton)`
  border-color: ${({ theme }) => theme.colors.buttonFormRed};
  width: 130px;
  margin-right: 1rem;
  color: ${({ theme }) => theme.colors.buttonFormRed};
  background: white;
`;

const StyledSaveButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const RecipientFieldRow = styled(FormRow)`
  margin-top: 0;
`;

const RecipientForm = () => {
  const location = useLocation();
  const { row, agency, program, action, list } = location.state;

  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [atcOptions, setAtcOptions] = useState([]);
  const [facilityType, setFacilityType] = useState(null);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [selectedAtc, setSelectedAtc] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedTVI, setSelectedTVI] = useState(null);
  const [TVIOptions, setTVIOptions] = useState([]);
  const [TVICourses, setTVICourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isLegacyData, setIsLegacyData] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const lastAtcParamsRef = useRef(null);

  const [formFields, setFormFields] = useState({
    ...(list !== "tvi" ? row : {}),
    allocationContactNumber: getFormattedContactNumber(
      row?.allocationContactNumber
    ),
  });

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { user } = useAuth();

  const role = user?.type || null;

  const handleSearchRecipientOnChange = (option) => {
    setFormFields({
      ...formFields,
      recipient: {
        firstName: option?.firstName,
        lastName: option?.lastName,
        middleInitial: option?.middleInitial,
        fullName: option?.name,
        contact: option?.contact,
        id: option.id,
      },
    });
  };

  const cannotEditStatus = (status) => {
    let cannotEdit = true;

    if (
      SUPER_ADMIN_ACCOUNTS.includes(role) &&
      status !== ALLOCATION_STATUS.PROCESSED &&
      status !== ALLOCATION_STATUS.INPROGRESS &&
      status !== ALLOCATION_STATUS.DOWNLOADED
    ) {
      cannotEdit = false;
    } else if (
      role === ACCOUNT_AGENCY &&
      status !== ALLOCATION_STATUS.DISAPPROVED &&
      status !== ALLOCATION_STATUS.PENDING
    ) {
      cannotEdit = false;
    } else if (
      role === ACCOUNT_EDITOR &&
      (status === ALLOCATION_STATUS.REQUESTED ||
        status === ALLOCATION_STATUS.PENDING ||
        status === ALLOCATION_STATUS.DISAPPROVED)
    ) {
      cannotEdit = false;
    }

    return cannotEdit;
  };

  const handleFailedRowEdit = () => {
    setFormFields(row);
  };

  const handleRemarks = (text) => {
    genericPost({
      url: "/comment",
      formData: {
        text,
        recipientId: formFields.id,
        newsId: null,
      },
      callback: () => {
        //
      },
    });

    genericPost({
      url: "/recipient/send-resubmission-notification",
      formData: {
        remarks: text,
        recipientId: formFields.id,
      },
      callback: () => {
        //
      },
    });
  };

  const updateRecipientDetails = (formData, recipientId) => {
    updateRow({
      url: `/recipient/${recipientId}`,
      formData,
      callback: ({ isSuccess }) => {
        setIsLoading(false);

        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated recipient details",
            });

            if (
              formFields.status === ALLOCATION_STATUS.RESUBMISSION &&
              congRoles.includes(role)
            ) {
              handleRemarks(formData.remarks);
            }

            if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
              dispatch(fetchPendingProjects());
            }

            const path = window.location.pathname.split("/recipient")[0];

            navigate(path, {
              state: {
                agencyId: agency,
              },
            });
          }, 1500);
        } else {
          handleFailedRowEdit();
        }
      },
    });
  };

  const isProjectTitleShown =
    (Number(agency) === 3 &&
      Number(program) === 14 &&
      (formFields?.allocationType?.id === 1 ||
        formFields?.allocationType?.id === 4 ||
        formFields?.allocationType?.id === 3)) ||
    (Number(agency) === 14 && formFields?.allocationType?.id !== 2);

  const isTrackingCodeShown =
    Number(agency) === 3 &&
    Number(program) === 14 &&
    withOtherInfo.includes(formFields?.allocationType?.id);

  const isFundSourceShown =
    [...canEditRoles, ...congRoles].includes(role) &&
    ((Number(agency) === 14 && formFields?.allocationType?.id !== 2) ||
      (Number(agency) === 3 &&
        Number(program) === 14 &&
        formFields?.allocationType?.id !== 2));

  const isSupportingDocumentsShown =
    (Number(agency) === 14 && formFields?.allocationType?.id !== 2) ||
    (Number(agency) === 27 &&
      (Number(program) === 10 || Number(program) === 15));

  const isTesdaTViShown =
    Number(agency) === 27 &&
    formFields?.allocationType?.id !== 2 &&
    (Number(program) === 10 || Number(program) === 15);

  const isSupportingTesdaDocsShown =
    Number(agency) === 27 && (Number(program) === 10 || Number(program) === 15);

  const handleOnSubmit = async () => {
    const isValid = await validate({
      formFields,
      agency: Number(agency),
      program,
      user,
      selectedCourses,
      uploadedFiles,
    });
    let remarks = "";

    if (typeof isValid === "boolean") {
      if (
        formFields.status === ALLOCATION_STATUS.RESUBMISSION &&
        congRoles.includes(role)
      ) {
        const result = await Swal.fire({
          title: "Are you sure you want to proceed?",
          text: "You can only edit this once",
          icon: "warning",
          confirmButtonText: "OK",
          showCancelButton: true,
        });

        if (result.isConfirmed) {
          const { value: text } = await Swal.fire({
            input: "textarea",
            inputLabel: "Remarks",
            inputPlaceholder: "Type your remarks here...",
            inputAttributes: {
              "aria-label": "Type your remarks here",
            },
            showCancelButton: true,
          });

          if (text) {
            remarks = text;
          } else return;
        } else {
          return;
        }
      }

      setIsLoading(true);

      const files = uploadedFiles?.map((file) => file.location) || [];

      const getInstitution = () => {
        let institution = null;

        if (formFields.allocationType?.id !== 2 && Number(agency) !== 8) {
          if (
            Number(agency) === 27 &&
            (Number(program) === 10 || Number(program) === 15)
          ) {
            institution =
              formFields.institution?.value ?? formFields.institution?.id;
          }

          if (Number(agency) === 14) {
            institution =
              typeof formFields?.institution === "string"
                ? formFields.institution
                : formFields.institution?.id;
          }

          if (Number(agency) === 3 && Number(program) === 14) {
            institution =
              typeof formFields?.institution === "string"
                ? formFields.institution
                : formFields.institution?.id;
          }
        } else if (Number(agency) === 8) {
          institution =
            formFields.institution?.value ?? formFields.institution?.id;
        } else if (
          Number(agency) === 3 &&
          Number(program) === 14 &&
          formFields.allocationType?.id === 3
        ) {
          institution = formFields.institution;
        }

        return institution;
      };

      const formData = {
        allocationContactName: withAllocationDetails.includes(
          formFields.allocationType?.id
        )
          ? formFields?.allocationContactName
          : null,
        allocationContactNumber: withAllocationDetails.includes(
          formFields.allocationType?.id
        )
          ? formFields?.allocationContactNumber?.slice(1)
          : null,
        allocationName:
          formFields.allocationType?.id === 2
            ? formFields?.allocationName
            : null,
        allocationReferenceKey:
          formFields.allocationType?.id === 2 ? selectedAtc?.id : null,
        allocation:
          formFields.allocationType?.id === 2
            ? {
                atcId: formFields.allocation?.atcId,
                principalId: formFields.allocation?.principalId,
                principal: {
                  manager: {
                    firstName: formFields?.allocation?.managerFirstName,
                    lastName: formFields?.allocation?.managerLastName,
                    contact: formFields?.allocation?.managerContact,
                  },
                  partner: {
                    firstName: formFields?.allocation?.partnerFirstName,
                    lastName: formFields?.allocation?.partnerLastName,
                    contact: formFields?.allocation?.partnerContact,
                  },
                  cos: {
                    firstName: formFields?.allocation?.CosFirstName,
                    lastName: formFields?.allocation?.CosLastName,
                    contact: formFields?.allocation?.CosContact,
                  },
                },
              }
            : null,
        // eslint-disable-next-line camelcase
        budget: formFields.budget_allocation,
        fundSource: isFundSourceShown ? formFields?.fundSource || null : null,
        implementingAgency: formFields.implementing?.id,
        implementingOffice:
          formFields.allocationType?.id !== 2 && Number(agency) === 14
            ? formFields?.implementingOffice
            : null,
        justification: formFields?.justification || null,
        institution: getInstitution(),
        ownership:
          Number(agency) === 8 &&
          typeof formFields?.institution?.value === "string"
            ? facilityType?.value
            : null,
        municipalityId:
          selectedMunicipality?.value !== "All Municipalities"
            ? formFields?.municipality?.id
            : null,
        provinceId:
          selectedProvince?.value !== "All Provinces"
            ? formFields?.province?.id
            : null,
        recipient: formFields.recipient,
        slots: formFields.slots,
        otherInfo: isTrackingCodeShown ? formFields?.otherInfo || null : null,
        documents: isSupportingDocumentsShown ? { supporting: files } : null,
        editorNotes: formFields?.notes
          ?.map((note) => note?.value?.trim())
          .join(";"),
        remarks,
        officialPosition: formFields?.officialPosition,
        officialId: formFields?.officialId,
        tviId:
          formFields.allocationType?.id !== 2 &&
          Number(agency) === AGENCY_TESDA_ID
            ? formFields?.tvi
            : null,
        courses:
          formFields.allocationType?.id !== 2 &&
          Number(agency) === AGENCY_TESDA_ID
            ? selectedCourses?.map((course) => ({
                id: course.id,
                slots: course.slots,
                tviCourseId: course?.tviCourseId,
              })) || []
            : null,
        status: formFields.status,
      };

      if (
        formFields.status === ALLOCATION_STATUS.RESUBMISSION &&
        congRoles.includes(role)
      ) {
        formData.isEdited = 1;
      }

      if (
        Number(agency) === 8 &&
        typeof formFields?.institution?.value === "string"
      ) {
        formData.ownership = facilityType?.value;
      }

      updateRecipientDetails(formData, formFields.id);
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const fetchRegions = () => {
    fetchAllRegions({
      params: {
        includeCentral: true,
      },
      callback: (data) => {
        if (data?.length !== 0) {
          const newData = data.filter((region) => region.id !== 18);
          const options = getFormattedOptions("acronym", newData);

          setRegions(options);
          setIsLoading(false);

          const region = options?.find(
            (option) => option.id === formFields?.implementing?.id
          );

          if (region) {
            setSelectedRegion(region);
          } else {
            setFormFields({
              ...formFields,
              implementing: null,
            });
          }
        }
      },
    });
  };

  const fetchAllProvinces = () => {
    fetchProvinces({
      param: formFields?.implementing?.id,
      callback: (data) => {
        const options = data.map(({ id, name, omegaId }) => ({
          label: name,
          value: id,
          omegaId,
        }));

        options.unshift({
          label: "All Provinces",
          value: "All Provinces",
        });

        const option = options?.find(
          (op) => op.value === formFields?.province?.id
        );

        if (option) setSelectedProvince(option);
        else setSelectedProvince(options[0]);

        setProvinceOptions(options);
      },
    });
  };

  const fetchAllMunicipalities = async () => {
    await fetchMunicipalities({
      param: selectedProvince?.value,
      callback: (data) => {
        if (data?.length) {
          // eslint-disable-next-line no-shadow
          const options = data.map(({ id, name, omegaId }) => ({
            label: name,
            value: id,
            omegaId,
          }));

          options.unshift({
            label: "All Municipalities",
            value: "All Municipalities",
          });

          const option = options?.find(
            (op) => op.value === formFields?.municipality?.id
          );

          if (option) setSelectedMunicipality(option);
          else {
            setSelectedMunicipality(options[0]);
          }
          setMunicipalityOptions(options);
        } else {
          setMunicipalityOptions([]);
        }
      },
    });
  };

  const fetchAllInstitutions = async () => {
    const param =
      // eslint-disable-next-line no-nested-ternary
      Number(agency) === 8
        ? formFields?.implementing?.id
          ? `?ownership=${facilityType?.value}&type=Healthcare&regionId=${
              formFields?.implementing?.id || null
            } `
          : `?ownership=${facilityType?.value}&type=Healthcare`
        : "?type=Educational";

    await fetchInstitutions({
      param,
      callback: (data) => {
        if (data.length) {
          const options = data.map((hospital) => ({
            ...hospital,
            id: hospital.id,
            label: hospital.name,
            value: hospital.id,
          }));

          setFacilityOptions(options);
        }
      },
    });
  };

  const fetchAllATC = async () => {
    setIsLoading(true);

    // eslint-disable-next-line camelcase
    const params = {
      // eslint-disable-next-line camelcase
      region_id: formFields?.implementing?.omegaId || null,
      // eslint-disable-next-line camelcase
      province_id:
        formFields?.province?.value !== "All Provinces"
          ? formFields?.province?.omegaId
          : null,
      // eslint-disable-next-line camelcase
      municipality_id:
        formFields?.municipality?.value !== "All Municipality"
          ? formFields?.municipality?.omegaId
          : null,
    };

    const paramsKey = JSON.stringify(params);

    if (lastAtcParamsRef.current === paramsKey) return;

    lastAtcParamsRef.current = paramsKey;

    await fetchAtcOptions({
      params,
      callback: (data) => {
        const options = data
          ?.filter((atc) => atc.principal)
          ?.map((atc) => ({
            ...atc,
            label: `${atc?.last_name}, ${atc?.first_name}`,
            value: atc?.id,
          }));

        const option = options?.find(
          (op) => op?.label === formFields?.allocationName
        );

        if (option) setSelectedAtc(option);

        setAtcOptions(options);
        setIsLoading(false);
      },
    });
  };

  const handleCongressmanSearch = _.debounce((inputValue, callback) => {
    congressmanSearch({
      inputValue,
      callback,
      isLegacyData,
    });
  }, 1000);

  const handleOfficialSearch = _.debounce((inputValue, callback) => {
    officialSearch({
      position: selectedOption.label,
      inputValue,
      callback,
    });
  }, 1000);

  const handleUploadedFiles = (link) => {
    setUploadedFiles((prevUploadedFiles) => {
      const fileIndex = prevUploadedFiles.findIndex(
        (file) => file.name === link.name
      );

      if (fileIndex !== -1) {
        const updatedFiles = [...prevUploadedFiles];
        updatedFiles[fileIndex] = link;

        return updatedFiles;
      }

      if (prevUploadedFiles.length < 5) {
        return [...prevUploadedFiles, link];
      }

      Toast({
        type: 4,
        content: "Exceeded file upload limit of 5",
      });

      return prevUploadedFiles;
    });
  };

  const handleDeleteFile = (file) => {
    const newUploadedFiles = uploadedFiles.filter(
      (files) => files.name !== file
    );
    setUploadedFiles(newUploadedFiles);
  };

  const setSupportingDocuments = () => {
    const files = [];

    row?.documents?.supporting?.forEach((fileURL) => {
      const fileArray = fileURL.split("/");

      const file = {
        name: decodeURIComponent(fileArray[fileArray.length - 1]),
        location: fileURL,
        percent: 100,
      };

      files.push(file);
    });

    setUploadedFiles(files);
  };

  const fetchTViOptions = () => {
    genericGet({
      url: "/tvi",
      params: {
        provinceId: selectedProvince.value,
        limit: -1,
      },
      callback: (data) => {
        if (data.rows?.length) {
          const options = getFormattedOptions("name", data.rows);

          setTVIOptions(options);

          const course = options?.find(
            (_course) => _course.id === formFields?.tviId
          );

          if (course) {
            setSelectedTVI(course);

            setFormFields({
              ...formFields,
              tvi: course?.value,
            });
          }
        } else {
          setTVIOptions([]);
        }
      },
    });
  };

  const fetchAllCourses = () => {
    genericGet({
      url: `/tvi/${selectedTVI?.value}/courses`,
      callback: (data) => {
        setTVICourses(data.rows);
      },
    });
  };

  const fetchTViCourses = () => {
    genericGet({
      url: `/tvi/recipient/${row?.id}/courses`,
      callback: (data) => {
        const courses = (data.rows || []).map((course) => ({
          ...course,
          oldSlotsValue: course.slots,
        }));
        setTVICourses(courses);
        setSelectedCourses(courses);
      },
    });
  };

  const setFormFromTvi = () => {
    setFormFields({
      receivingEntity: row.receivingEntity,
      allocation: row.allocation,
      allocationReferenceKey: row.allocationReferenceKey,
      allocationName: row.allocationName,
      allocationContactName: row.allocationContactName,
      allocationContactNumber: getFormattedContactNumber(
        row.allocationContactNumber
      ),
      documents: row.documents,
      justification: row.justification,
      // eslint-disable-next-line camelcase
      congressman_id: row.congressmanId,
      recipient: row.recipient,
      agency: "TESDA",
      // eslint-disable-next-line camelcase
      budget_allocation: row.budget_allocation,
      editorNotes: row.editorNotes,
      officialId: row.officialId,
      officialPosition: row.officialPosition,
      tviId: row.tviId,
      proponentType: row.proponentType,
      tviEndorsedVia: row.tviEndorsedVia,
      implementing: {
        id: row.regionId,
        region: row.region,
      },
      allocationType: row.allocationType,
      province: {
        id: row.provinceId,
        name: row.province,
      },
      municipality: {
        id: row.municipalityId,
        name: row.municipality,
      },
      status: row.status,
      dateRequested: row.updatedAt,
      id: row.id,
    });
  };

  useEffect(() => {
    if (row?.editorNotes) {
      const notes = row.editorNotes.split(";").map((note) => ({
        label: note,
        value: note,
      }));

      setFormFields({
        ...formFields,
        notes,
      });
    }

    if (list === "tvi") setFormFromTvi();
  }, [row]);

  useEffect(() => {
    if (formFields?.implementing?.id != null) {
      setIsLoading(true);
      fetchRegions();
    }
  }, [formFields.implementing?.id]);

  useEffect(() => {
    if (row?.documents?.supporting?.length !== 0) {
      setSupportingDocuments();
    }

    setIsLoading(true);
    fetchAllATC();

    if (new Date(row?.dateRequested) < BEFORE_TWENTIETH) {
      setIsLegacyData(true);
    }
  }, []);

  useEffect(() => {
    if (selectedRegion) {
      fetchAllProvinces();
      fetchAllATC();

      setTVIOptions([]);
      setTVICourses([]);
      setSelectedCourses([]);
      setSelectedTVI(null);

      if (Number(agency) === 8) fetchAllInstitutions();
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (selectedProvince) {
      if (selectedProvince.value !== "All Provinces") fetchAllMunicipalities();
      setSelectedMunicipality(null);
      fetchAllATC();

      setTVIOptions([]);
      setTVICourses([]);
      setSelectedCourses([]);
      setSelectedTVI(null);

      if (
        typeof selectedProvince?.value === "number" &&
        Number(agency) === AGENCY_TESDA_ID
      ) {
        fetchTViOptions();
      }

      if (selectedProvince.value === "All Provinces") {
        setSelectedMunicipality({
          label: "All Municipalities",
          value: "All Municipalities",
        });
      }
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (selectedMunicipality) {
      fetchAllATC();
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (
      formFields?.allocationType?.id === 2 &&
      selectedAtc &&
      !row?.allocationContactName
    ) {
      const { principal } = selectedAtc;
      const firstName = principal?.atc_partner_first_name;
      const lastName = principal?.atc_partner_last_name;

      setFormFields((prev) => ({
        ...prev,
        allocationContactName: lastName
          ? `${firstName} ${lastName}`
          : firstName,
        allocationContactNumber: getFormattedContactNumber(
          principal?.atc_partner_contact
        ),
      }));
    }
  }, [selectedAtc]);

  useEffect(() => {
    if (Number(agency) === AGENCY_TESDA_ID) {
      fetchAllInstitutions();
    }

    if (Number(agency) === 8) {
      if (formFields?.institution) {
        const option = institutionTypes?.find(
          (op) => op.label === formFields?.institution?.ownership
        );

        if (option) setFacilityType(option);
      } else {
        setFacilityType(institutionTypes[0]);
      }
    }
  }, [agency]);

  useEffect(() => {
    if (facilityType) {
      fetchAllInstitutions();
    }
  }, [facilityType]);

  useEffect(() => {
    if (selectedTVI) {
      setSelectedCourses([]);

      if (row?.tviId !== selectedTVI?.value) {
        fetchAllCourses();
      } else {
        fetchTViCourses();
      }
    }
  }, [selectedTVI]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Formik
      initialValues={formFields}
      enableReinitialize
      onSubmit={handleOnSubmit}
    >
      {({ handleChange, values }) => (
        <Form id="recipient-form">
          {formFields?.recipient && (
            <RecipientFieldRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Recipient"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the house member's name"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <AsyncSelect
                  placeholder=""
                  styles={addProjectSearchInputStyle}
                  value={{
                    // eslint-disable-next-line max-len
                    label: `${formFields?.recipient?.lastName}, ${formFields?.recipient?.firstName}`,
                    // eslint-disable-next-line max-len
                    value: `${formFields?.recipient?.lastName}, ${formFields?.recipient?.firstName}`,
                  }}
                  onChange={(option) => {
                    handleSearchRecipientOnChange(option);
                  }}
                  loadOptions={handleCongressmanSearch}
                  isDisabled={action !== EDIT_ACTION}
                />
              </Col>
            </RecipientFieldRow>
          )}

          {formFields?.project && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>{"Program"}</FormLabel>
                <p>{"Provide name of the program"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <FormGenerator
                  type="text"
                  name="program"
                  value={values?.project}
                  isDisabled
                />
              </Col>
            </FormRow>
          )}

          {isProjectTitleShown && (
            <Field name="projectTitle">
              {({ form: { touched, errors } }) => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Project Title"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the project title"}</p>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormGenerator
                      className={classNames({
                        "is-invalid":
                          errors?.institution && touched?.institution,
                      })}
                      placeholder="Project Title"
                      type="text"
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;

                        handleChange(value);

                        setFormFields({
                          ...formFields,
                          institution: value,
                        });
                      }}
                      value={formFields?.institution?.name}
                      isDisabled={action !== EDIT_ACTION}
                    />

                    <ErrorMessage name="projectTitle">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          {isTrackingCodeShown && (
            <Field name="otherInfo">
              {({ form: { touched, errors } }) => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"LGSF Tracking Code"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the  LGSF tracking code"}</p>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormGenerator
                      className={classNames({
                        "is-invalid": errors?.otherInfo && touched?.otherInfo,
                      })}
                      placeholder="LGSF Tracking Code"
                      type="text"
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;

                        handleChange(value);

                        setFormFields({
                          ...formFields,
                          otherInfo: value,
                        });
                      }}
                      value={formFields?.otherInfo}
                      isDisabled={action !== EDIT_ACTION}
                    />

                    <ErrorMessage name="otherInfo">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          <Field name="budget">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Amount Requested"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the requested amount"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="number"
                    placeholder="Amount"
                    className={classNames({
                      "is-invalid":
                        errors?.budget_allocation && touched?.budget_allocation,
                    })}
                    decimalSeparator="."
                    thousandSeparator=","
                    onValueChange={(value) => {
                      const { floatValue } = value;

                      setFormFields({
                        ...formFields,
                        // eslint-disable-next-line camelcase
                        budget_allocation: floatValue,
                      });
                    }}
                    value={formFields?.budget_allocation}
                    disabled={
                      action !== EDIT_ACTION ||
                      formFields.status === ALLOCATION_STATUS.RESUBMISSION ||
                      (Number(agency) === AGENCY_TESDA_ID &&
                        ((!SUPER_ADMIN_ACCOUNTS.includes(role) &&
                          row.tviId === null) ||
                          row.tviId !== null))
                    }
                  />

                  <ErrorMessage name="budget">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Allocation Type"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the allocation type"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <FormGenerator
                type="text"
                name="lastName"
                value={values?.allocationType?.name}
                isDisabled
              />
            </Col>
          </FormRow>

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Status"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the status of your budget"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <CustomSelect
                placeholder=""
                name="status"
                isSearchable={false}
                styles={addProjectDropdownStyles}
                value={ALLOCATION_STATUS_OPTIONS.find(
                  (stat) => stat.value === formFields?.status
                )}
                onChange={(option) => {
                  setFormFields({
                    ...formFields,
                    status: option.value,
                  });
                }}
                options={getStatusOptions(formFields?.status, role)}
                getOptionLabel={(option) => {
                  return option?.displayLabel;
                }}
                isDisabled={
                  !canEditRoles.includes(role) ||
                  cannotEditStatus(formFields.status) ||
                  action !== EDIT_ACTION
                }
              />
            </Col>
          </FormRow>

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Recommended Implementing Agency"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the recommended implementing agency"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <CustomSelect
                placeholder="Please Select Region"
                name="implementingAgency"
                isSearchable
                styles={addProjectDropdownStyles}
                onChange={(option) => {
                  setSelectedRegion(option);

                  setFormFields({
                    ...formFields,
                    implementing: {
                      id: option?.value,
                      region: option?.label,
                      omegaId: option?.omegaId,
                    },
                  });
                }}
                value={regions?.find(
                  (region) => region.value === formFields?.implementing?.id
                )}
                options={regions}
                isDisabled={action !== EDIT_ACTION}
              />
            </Col>
          </FormRow>

          {Number(agency) === 14 && formFields?.allocationType?.id !== 2 && (
            <Field name="implementingOffice">
              {({ form: { touched, errors } }) => (
                <FormRow>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Implementing Office"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the implementing office"}</p>
                  </Col>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormGenerator
                      className={classNames({
                        "is-invalid":
                          errors?.implementingOffice &&
                          touched?.implementingOffice,
                      })}
                      placeholder="Implementing Office"
                      type="text"
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;

                        handleChange(value);

                        setFormFields({
                          ...formFields,
                          implementingOffice: value,
                        });
                      }}
                      value={formFields?.implementingOffice}
                      isDisabled={action !== EDIT_ACTION}
                    />

                    <ErrorMessage name="implementingOffice">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </Col>
                </FormRow>
              )}
            </Field>
          )}

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Province"}
                {Number(agency) === AGENCY_TESDA_ID && RequiredIndicator}
              </FormLabel>
              <p>{"Provide the province in your region"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <CustomSelect
                placeholder="Please Select a Province"
                name="province"
                isSearchable
                styles={addProjectDropdownStyles}
                onChange={(option) => {
                  setSelectedProvince(option);

                  setFormFields({
                    ...formFields,
                    province: {
                      id: option?.value,
                      name: option?.label,
                      omegaId: option?.omegaId,
                    },
                  });
                }}
                value={selectedProvince}
                options={provinceOptions}
                isDisabled={action !== EDIT_ACTION}
              />
            </Col>
          </FormRow>

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>{"Municipality"}</FormLabel>
              <p>{"Provide the municipality in your province"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <CustomSelect
                placeholder="Please Select a Municipality"
                name="municipality"
                isSearchable
                styles={addProjectDropdownStyles}
                onChange={(option) => {
                  setSelectedMunicipality(option);

                  setFormFields({
                    ...formFields,
                    municipality: {
                      id: option?.value,
                      name: option?.label,
                      omegaId: option?.omegaId,
                    },
                  });
                }}
                value={selectedMunicipality}
                options={municipalityOptions}
                isDisabled={action !== EDIT_ACTION}
              />
            </Col>
          </FormRow>

          {canEditRoles.includes(role) && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>{"Editor Notes"}</FormLabel>
                <p>{"Provide editor notes"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomCreatableSelect
                  isMulti
                  placeholder="Editor Notes"
                  name="notes"
                  styles={addProjectDropdownStyles}
                  onChange={(option) => {
                    setFormFields({
                      ...formFields,
                      notes: option,
                    });
                  }}
                  value={formFields?.notes}
                  isDisabled={action !== EDIT_ACTION}
                />
              </Col>
            </FormRow>
          )}

          {formFields?.allocationType?.id === 2 && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"ATC"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the ATC Branch"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomSelect
                  placeholder="ATC"
                  name="atc"
                  styles={addProjectDropdownStyles}
                  onChange={(option) => {
                    if (
                      !option?.principal?.atc_partner_first_name ||
                      option?.principal?.atc_partner_contact?.length < 10
                    ) {
                      Toast({
                        type: 4,
                        content: "ATC contact name and number cannot be empty",
                      });

                      return;
                    }
                    setSelectedAtc(option);

                    setFormFields({
                      ...formFields,
                      // eslint-disable-next-line max-len
                      allocationName: `${option?.last_name}, ${option?.first_name}`,
                      allocationContactNumber: getFormattedContactNumber(
                        option?.principal?.atc_partner_contact
                      ),
                      allocationContactName: option?.principal
                        ?.atc_partner_last_name
                        ? // eslint-disable-next-line max-len
                          `${option?.principal?.atc_partner_first_name} ${option?.principal?.atc_partner_last_name}`
                        : option?.principal?.atc_partner_first_name,
                      allocationReferenceKey: option?.principal?.id,
                      allocation: {
                        atcId: option?.id,
                        principalId: option?.principal?.id,
                        managerFirstName:
                          option?.principal?.atc_partner_manager_first_name,
                        managerLastName:
                          option?.principal?.atc_partner_manager_last_name,
                        managerContact:
                          option?.principal?.atc_partner_manager_contact,
                        partnerFirstName:
                          option?.principal?.atc_partner_first_name,
                        partnerLastName:
                          option?.principal?.atc_partner_last_name,
                        partnerContact: option?.principal?.atc_partner_contact,
                        CosFirstName:
                          option?.principal?.atc_partner_cos_first_name,
                        CosLastName:
                          option?.principal?.atc_partner_cos_last_name,
                        CosContact: option?.principal?.atc_partner_cos_contact,
                      },
                    });
                  }}
                  value={selectedAtc}
                  options={atcOptions}
                  isDisabled={action !== EDIT_ACTION}
                />
              </Col>
            </FormRow>
          )}

          {Number(agency) === 8 && (
            <>
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Type of Facility"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the type of facility"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <CustomSelect
                    placeholder="Type of Facility"
                    name="facility"
                    styles={addProjectDropdownStyles}
                    onChange={(option) => {
                      setFacilityType(option);
                    }}
                    value={facilityType}
                    options={institutionTypes}
                    isDisabled={action !== EDIT_ACTION}
                  />
                </Col>
              </FormRow>

              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Name of Facility"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the name of facility"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <CustomCreatableSelect
                    placeholder="Name of Facility"
                    name="facility"
                    styles={addProjectDropdownStyles}
                    onChange={(option) => {
                      setFormFields({
                        ...formFields,
                        institution: option,
                      });
                    }}
                    value={facilityOptions?.find(
                      (facility) => facility.id === formFields?.institution?.id
                    )}
                    options={facilityOptions}
                    isDisabled={action !== EDIT_ACTION}
                  />
                </Col>
              </FormRow>
            </>
          )}

          {isTesdaTViShown && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Name of TVI"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the name of the TVI"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                {row?.tviId !== null ? (
                  <CustomSelect
                    placeholder="Name of TVI"
                    name="tvi"
                    styles={addProjectDropdownStyles}
                    onChange={(option) => {
                      setSelectedTVI(option);

                      setFormFields({
                        ...formFields,
                        tvi: option?.value,
                      });
                    }}
                    value={selectedTVI}
                    options={TVIOptions}
                    isDisabled={action !== EDIT_ACTION}
                  />
                ) : (
                  <FormGenerator
                    name="institution"
                    placeholder="TVI"
                    type="text"
                    value={formFields?.institution?.name}
                    isDisabled
                  />
                )}
              </Col>
            </FormRow>
          )}

          {isFundSourceShown && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Fund Source"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide the fund source"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomSelect
                  placeholder="Fund Source"
                  name="fundSource"
                  isSearchable
                  styles={addProjectDropdownStyles}
                  onChange={(option) => {
                    setFormFields({
                      ...formFields,
                      fundSource: option?.label,
                    });
                  }}
                  value={
                    Number(agency) === 14
                      ? fundSourceOptions.find(
                          (options) => options.label === formFields?.fundSource
                        )
                      : fundSourceDBMOptions.find(
                          (options) => options.label === formFields?.fundSource
                        )
                  }
                  options={
                    Number(agency) === 14
                      ? fundSourceOptions
                      : fundSourceDBMOptions
                  }
                  isDisabled={action !== EDIT_ACTION}
                />
              </Col>
            </FormRow>
          )}

          {Number(agency) === 14 && formFields?.allocationType?.id !== 2 && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>{"Supporting Documents"}</FormLabel>
                <p>{"Provide supporting documents"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomDropzone
                  handleUploadedFiles={handleUploadedFiles}
                  handleDeleteFile={handleDeleteFile}
                  uploadedFiles={uploadedFiles}
                  isDisabled={action !== EDIT_ACTION}
                  label="Upload Attachments"
                  acceptedFileTypes={acceptedFileTypes}
                  acceptedFileTypeErrorMsg="Accepted files are only xls, xlxs, and pdf."
                />
              </Col>
            </FormRow>
          )}

          {isSupportingTesdaDocsShown && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>
                  {"Supporting Documents"}
                  {RequiredIndicator}
                </FormLabel>
                <p>{"Provide supporting documents"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <CustomDropzone
                  handleUploadedFiles={handleUploadedFiles}
                  handleDeleteFile={handleDeleteFile}
                  uploadedFiles={uploadedFiles}
                  isDisabled={action !== EDIT_ACTION}
                  label="Upload Attachments"
                />
              </Col>
            </FormRow>
          )}

          <Field name="justification">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Justification"}</FormLabel>
                  <p>{"Provide the justification"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    className={classNames({
                      "is-invalid":
                        errors?.justification && touched?.justification,
                    })}
                    rows="5"
                    type="textarea"
                    placeholder="Enter Text Here"
                    onChange={(event) => {
                      const {
                        target: { value },
                      } = event;

                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        justification: value,
                      });
                    }}
                    value={formFields?.justification || ""}
                    isDisabled={action !== EDIT_ACTION}
                  />

                  <ErrorMessage name="justification">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          {formFields?.allocationType?.id === 3 && (
            <>
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Position"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the recipient position"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <CustomSelect
                    onChange={(option) => {
                      setSelectedOption(option);

                      if (formFields.allocationContactName !== "") {
                        formFields.allocationContactName = "";
                        formFields.allocationContactNumber = "0";
                      }

                      setFormFields({
                        ...formFields,
                        officialPosition: option.value,
                      });
                    }}
                    value={selectorOptions.find(
                      (position) =>
                        position.value === formFields?.officialPosition
                    )}
                    options={selectorOptions}
                    styles={addProjectDropdownStyles}
                    isDisabled={action !== EDIT_ACTION}
                  />
                </Col>
              </FormRow>

              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Recipient Contact"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide the recipient contact"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <AsyncSelect
                    placeholder="Official"
                    onChange={(option) => {
                      setFormFields({
                        ...formFields,
                        allocationContactName: option.label,
                        allocationContactNumber: `0${option.contact}`,
                        officialId: option.value,
                      });
                    }}
                    loadOptions={handleOfficialSearch}
                    styles={addProjectSearchInputStyle}
                    value={{
                      label: `${formFields?.allocationContactName}`,
                      // eslint-disable-next-line max-len
                      value: `${formFields?.allocationContactName}`,
                    }}
                    isDisabled={action !== EDIT_ACTION}
                  />

                  <ErrorMessage name="recipientContact">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>

              <Field name="mobile">
                {({ form: { touched, errors } }) => (
                  <FormRow>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormLabel>
                        {"Contact Number"}
                        {RequiredIndicator}
                      </FormLabel>
                      <p>{"Provide the number of the recipient"}</p>
                    </Col>
                    <Col
                      xs="12"
                      md="6"
                    >
                      <FormGenerator
                        className={classNames({
                          "is-invalid":
                            errors?.allocationContactNumber &&
                            touched?.allocationContactNumber,
                        })}
                        name="mobile"
                        placeholder="Contact Number"
                        type="mobile"
                        onChange={(value) => {
                          handleChange(value);

                          setFormFields({
                            ...formFields,
                            allocationContactNumber: value,
                          });
                        }}
                        value={formFields?.allocationContactNumber}
                        isDisabled
                      />

                      <ErrorMessage name="mobile">
                        {(msg) => (
                          <small className="field-error text-danger">
                            {msg}
                          </small>
                        )}
                      </ErrorMessage>
                    </Col>
                  </FormRow>
                )}
              </Field>
            </>
          )}

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>{"Date Requested"}</FormLabel>
              <p>{"Provide date requested"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <FormGenerator
                type="text"
                name="lastName"
                value={getFormattedDateString(
                  values?.dateRequested,
                  "MMM-dd-yyyy"
                )}
                isDisabled
              />
            </Col>
          </FormRow>

          {formFields?.dateApproved && (
            <FormRow>
              <Col
                xs="12"
                md="6"
              >
                <FormLabel>{"Date Endorsed"}</FormLabel>
                <p>{"Provide date endorsed"}</p>
              </Col>
              <Col
                xs="12"
                md="6"
              >
                <FormGenerator
                  type="text"
                  name="dateApproved"
                  value={getFormattedDateString(
                    values?.dateApproved,
                    "MMM-dd-yyyy"
                  )}
                  isDisabled
                />
              </Col>
            </FormRow>
          )}

          {/* TVI Courses table starts here */}
          {Number(agency) === AGENCY_TESDA_ID ? (
            <CoursesTable
              data={TVICourses}
              selectedCourses={selectedCourses}
              setSelectedCourses={setSelectedCourses}
              isHidden
              isDisabled={action !== ADD_ACTION}
              form={formFields}
              setForm={setFormFields}
              action={action}
            />
          ) : null}
          {/* TVI Courses table end here */}

          {Number(agency) === AGENCY_DPWH_ID ? (
            <DpwhTable
              data={formFields?.projects}
              onAdd={() => {}}
              onEdit={() => {}}
              budgetAllocation={formFields?.budget_allocation}
              recipientId={formFields?.id}
            />
          ) : null}

          <ButtonsContainer>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() => {
                let path;

                if (list === "tvi") {
                  path = "/settings/tesda";
                } else if (list === "dpwh") {
                  path = "/dpwhlisting";
                } else {
                  const [basePath] =
                    window.location.pathname.split("/recipient");
                  path = basePath;
                }

                const options =
                  list !== "tvi" ? { state: { agencyId: agency } } : undefined;

                navigate(path, options);
              }}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading || action !== EDIT_ACTION}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </ButtonsContainer>
        </Form>
      )}
    </Formik>
  );
};

export default RecipientForm;
